/**
 * Input Controller — input handling, type detection, and input pane management.
 *
 * Owns: input store selectors, type detection with auto-sync, semantic type
 * dropdown logic, persist-input toggle, input visualizer overlay, and
 * InputPane rendering.
 */

import React, { useCallback, useEffect,useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ErrorBoundary } from '@/components/common/ErrorBoundary';
import { SEMANTIC_TYPES } from '@/detectors/semanticTypes';
import { useEffectiveVisualizerMode } from '@/hooks/useEffectiveVisualizerMode';
import { useTypeDetection } from '@/hooks/useTypeDetection';
import type { ISemanticType } from '@/interfaces/ISemanticType';
import { createFileInput, importInputFile } from '@/lib/fileUtils';
import { useAppStore } from '@/store/useAppStore';
import { useEditorStore } from '@/store/useEditorStore';
import { useTabStore } from '@/store/useTabStore';
import type { VisualizerMode } from '@/store/useVisualizerStore';

import { InputPane } from './InputPane';
import { PanelVisualizer } from './PanelVisualizer';
import { useVisualizerPanel } from './VisualizerManager';

export const InputController: React.FC = () => {
  const { t } = useTranslation('editor');
  const { setInput, rerunFromStep } = useAppStore();
  const input = useEditorStore((s) => s.input);
  const pipeline = useEditorStore((s) => s.pipeline);
  const setInputType = useEditorStore((s) => s.setInputType);
  const inputType = useEditorStore((s) => s.inputType);
  const inputTypeMode = useEditorStore((s) => s.inputTypeMode);
  const setInputTypeMode = useEditorStore((s) => s.setInputTypeMode);

  const activeTabId = useTabStore((s) => s.activeTabId);
  const persistInput = useTabStore(
    (s) => s.tabs.find((t) => t.id === s.activeTabId)?.persistInput ?? false,
  );
  const togglePersistInput = useTabStore((s) => s.togglePersistInput);
  const handleTogglePersistInput = useCallback(
    () => togglePersistInput(activeTabId),
    [togglePersistInput, activeTabId],
  );

  const FILE_IMPORT_ACCEPT = '.json,.csv,.tsv,.xml,.yaml,.yml,.html,.htm,.svg,.md,.markdown,.txt,.toml,.sql,.css,.js,.ts,.jsx,.tsx,text/*,application/json,application/xml,application/x-yaml';

  const handleImportFile = useCallback(() => {
    createFileInput(FILE_IMPORT_ACCEPT, async (file) => {
      const result = await importInputFile(file);
      setInput(result.content);
    });
  }, [setInput]);

  // Type detection with debouncing
  const { type: detectedType } = useTypeDetection(input, 200);

  // Sync detected type → store when in auto mode
  useEffect(() => {
    if (inputTypeMode !== 'auto') return;
    setInputType(detectedType);
  }, [detectedType, inputTypeMode, setInputType]);

  const [fullscreen, setFullscreen] = useState(false);

  const viz = useVisualizerPanel('input', inputType);
  const { pinnedStepId } = useEffectiveVisualizerMode('input');

  const editStepIndex = useMemo(() => {
    if (!pinnedStepId) return null;
    const index = pipeline.findIndex((step) => step.id === pinnedStepId);
    return index >= 0 ? index : null;
  }, [pipeline, pinnedStepId]);

  const handleVisualizerEdit = useCallback((newData: string) => {
    if (editStepIndex == null) {
      setInput(newData);
      return;
    }
    void rerunFromStep(editStepIndex, newData, { updateInput: true });
  }, [editStepIndex, rerunFromStep, setInput]);

  // Input visualizer overlay
  const visualizerOverlay = useMemo(() => {
    if (viz.storeMode === 'raw') return undefined;
    return (
      <ErrorBoundary fallback={<div className="p-4 text-destructive text-sm">{t('visualizer.crashed')}</div>}>
        <PanelVisualizer
          panelRole="input"
          content={input}
          semanticType={inputType}
          onEdit={handleVisualizerEdit}
          fullscreen={fullscreen}
          onExitFullscreen={() => setFullscreen(false)}
          className="flex-1 min-h-0"
          quickSwitchActiveModes={viz.activeModes}
          quickSwitchCurrentMode={viz.storeMode}
          onQuickSwitchMode={(mode) => viz.switchMode(mode)}
          onQuickSwitchRemove={(mode) => viz.removeMode(mode)}
          quickSwitchAvailableModes={viz.availableModes as VisualizerMode[]}
          onQuickSwitchAdd={(mode) => { viz.addActiveMode('input', mode); viz.switchMode(mode); }}
        />
      </ErrorBoundary>
    );
  }, [input, inputType, setInput, fullscreen, viz.storeMode, viz.activeModes, viz.availableModes, viz.switchMode, viz.removeMode, viz.addActiveMode]);

  // Semantic type dropdown options
  const semanticTypeOptions = useMemo(() => {
    const all = Object.values(SEMANTIC_TYPES)
      .slice()
      .sort((a, b) => a.semanticType.localeCompare(b.semanticType))
      .map((t) => ({ value: t.semanticType, label: t.semanticType }));
    const autoLabel = detectedType ? t('input.autoType', { type: detectedType.semanticType }) : t('input.auto');
    return [{ value: '__auto__', label: autoLabel }, ...all];
  }, [detectedType]);

  const selectedSemanticTypeValue =
    inputTypeMode === 'auto' ? '__auto__' : inputType?.semanticType ?? '__auto__';

  const semanticTypeByName = useCallback(
    (name: string): ISemanticType | null =>
      Object.values(SEMANTIC_TYPES).find((t) => t.semanticType === name) ?? null,
    [],
  );

  const handleSemanticTypeChange = (value: string) => {
    if (value === '__auto__') {
      setInputTypeMode('auto');
      setInputType(detectedType);
      return;
    }
    setInputTypeMode('manual');
    setInputType(semanticTypeByName(value));
  };

  return (
    <InputPane
      value={input}
      onChange={(v: string) => setInput(v)}
      onPaste={async () => { const t = await navigator.clipboard.readText(); setInput(t); }}
      onClear={() => setInput('')}
      onDragOver={(e) => e.preventDefault()}
      onDrop={(e) => e.preventDefault()}
      inputMimeType={null}
      semanticTypeOptions={semanticTypeOptions}
      selectedSemanticTypeValue={selectedSemanticTypeValue}
      onSemanticTypeChange={handleSemanticTypeChange}
      stats={null} /* SKELETON: IO stats — wire useIOStats hook when ready */
      visualizerOverlay={visualizerOverlay}
      detectedTypeName={detectedType?.semanticType}
      effectiveModeName={viz.effectiveModeName}
      isAutoDetectedType={inputTypeMode === 'auto'}
      canAddVisualizer={viz.canAdd}
      isExplicitVisualizerActive={viz.isExplicitActive}
      onAddVisualizerStep={viz.switchMode}
      onRemoveVisualizerStep={viz.resetMode}
      isVisualizerEditable={viz.isEditable}
      isFullscreen={fullscreen}
      onToggleFullscreen={() => setFullscreen((f) => !f)}
      quickSwitchActiveModes={viz.activeModes}
      quickSwitchCurrentMode={viz.storeMode}
      onQuickSwitchMode={(mode) => viz.switchMode(mode)}
      onQuickSwitchRemove={(mode) => viz.removeMode(mode)}
      quickSwitchAvailableModes={viz.availableModes as VisualizerMode[]}
      onQuickSwitchAdd={(mode) => { viz.addActiveMode('input', mode); viz.switchMode(mode); }}
      persistInput={persistInput}
      onTogglePersistInput={handleTogglePersistInput}
      onImportFile={handleImportFile}
    />
  );
};
